import React, { useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { IconButton } from 'src/components/Buttons';
import { EventHeaderIconWithHoverEffect } from 'src/components/Events/EventHeader';
import {
  EventSessionRole,
  useEventHubContext,
} from 'src/contexts/EventHubContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { createInventoryEventUrl } from 'src/navigations/Routes/InventoryEvent/InventoryEvent.utils';
import { CompanionModeEnterIcon, CompanionModeExitIcon } from 'src/svgs';
import { FullPageIcon } from 'src/svgs/FullPageIcon';
import { ContentId } from 'src/utils/constants/contentId';
import { Event, Feature } from 'src/WebApiController';

export const EventActionsCell = ({ event }: { event: Event }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { onSelectEvent, onCancelSession, sessionRole } = useEventHubContext();
  const hasCompanionModeInInsightViewFeature = useUserHasFeature(
    Feature.CompanionModeInInsightView
  );

  const eventUrl = createInventoryEventUrl(
    event.virtId,
    true // includeSearchParams
  );

  const onViewClick = useCallback(
    (eventUrl: string) => {
      if (event) {
        window.location.replace(`#${event.virtId}`);
        setTimeout(() => {
          navigate(eventUrl, {
            state: {
              returnUrl: `${location.pathname}${location.search}#${event.virtId}`,
            },
          });
        }, 10);
      }
    },
    [event, navigate, location.pathname, location.search]
  );

  const onEventCompanionSelect = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (sessionRole === EventSessionRole.Portfolio) {
        onCancelSession();
      } else {
        onSelectEvent(event.virtId);
      }
    },
    [event.virtId, onCancelSession, onSelectEvent, sessionRole]
  );

  return (
    <TableCellDiv align="right" showTooltip={false}>
      <Stack alignItems="center" gap="m">
        <Link
          to={eventUrl}
          onClick={(e) => {
            if (e.button === 0 && e.getModifierState('Control') === false) {
              // Perform like a button click when it's a left-click (and no Ctrl)
              // Else the click will be whatever <a /> does
              e.preventDefault();
              e.stopPropagation();

              onViewClick(eventUrl);
            }
          }}
        >
          <IconButton
            icon={
              <FullPageIcon
                fill={vars.color.textBrand}
                size={vars.iconSize.xs}
                withHoverEffect
              />
            }
            titleContentId={ContentId.OpenEventPage}
          />
        </Link>
        {hasCompanionModeInInsightViewFeature && (
          <EventHeaderIconWithHoverEffect
            IconComponent={
              sessionRole === EventSessionRole.Portfolio
                ? CompanionModeExitIcon
                : CompanionModeEnterIcon
            }
            onClick={onEventCompanionSelect}
            size={vars.iconSize.m}
            contentId={
              sessionRole === EventSessionRole.Portfolio
                ? ContentId.ExitCompanionMode
                : ContentId.EnterCompanionMode
            }
          />
        )}
      </Stack>
    </TableCellDiv>
  );
};
